import React, { useEffect } from "react";
import PropTypes from "prop-types";
import QuizTimer from "./QuizTimer";
import QuizHeaderLoader from "./../Loader/QuizHeaderLoader";
import { useStyles } from "sdk/custom-hooks";
import { useSelector } from "react-redux";
import SubmitButton from "./SubmitButton";
import { fullScreenMode } from "sdk/common";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import QuestionQuiz from "../../../assets/images/QuizIcon.png";
import pauseImage from "../../../assets/images/ShadowPauseButton.png";
import mobilePause from "../../../assets/images/mobilePause.png";
import moment from "moment";
import { Box } from "@material-ui/core";
import { LanguageSelect } from "../../../pages/LanguageSelect";
import i18n from 'sdk/translations';

function calculateNewTime(mode, selectTimeFor, duration, totalQuestions, totalMinutes) {
  const isQuestionTimer = selectTimeFor === "QUESTION";
  const questionBasedDuration = duration / totalQuestions;

  if (mode === "mock-test") {
    return isQuestionTimer ? questionBasedDuration : duration;
  }

  const totalMinutesAsNumber = Number.parseInt(totalMinutes, 10);
  const durationAsNumber = Number.parseInt(duration, 10);
  if (totalMinutesAsNumber > durationAsNumber) {
    return isQuestionTimer ? questionBasedDuration : duration;
  }

  return totalMinutes / totalQuestions;
}

function renderPauseImage(isPaused, source, className, pauseAlt, resumeAlt) {
  const altText = isPaused ? pauseAlt : resumeAlt;
  return <img src={source} alt={altText} className={className} />;
}

/**
 * Quiz header
 * @param props
 * @returns {*}
 * @constructor
 */
function QuizHeader(props) {
  const {
    quizPage,
    name,
    viewMode,
    mode,
    submitQuiz,
    drawerToggle,
    duration,
    usedTime,
    setTimeTaken,
    isLoading,
    toggleDrawerHandler,
    pauseQuiz,
    handleQuizPauseChange,
    slug,
    endDate,
    totalQuestions,
    selectTimeFor,
    handleClick,
    hideNext,
    reset,
    setReset,
    courseId
  } = props;
  const history = useHistory();
  const quiz = useSelector((state) => state.quiz);

  const selectedLanguage = useSelector((state) => {
    const lang = state.user.language || "en";
    return lang === "en" || lang === "hn" ? lang : "en";
  });

  const classes = useStyles(styles)();
  const headerClass =
    quizPage && drawerToggle ? classes.quizHeaderExpand : classes.quizHeader;
  const quizStartDate = new Date();
  const formattedStartDate = moment(quizStartDate).format("DD-MM-YYYY HH:mm");
  const formattedEndDate = moment(endDate).format("DD-MM-YYYY HH:mm");
  const convertedQuizEndDate = moment(formattedEndDate, "DD-MM-YYYY HH:mm:ss");
  const convertedQuizCurrentDateTime = moment(
    formattedStartDate,
    "DD-MM-YYYY HH:mm:ss"
  );
  const difference = moment.duration(
    convertedQuizEndDate.diff(convertedQuizCurrentDateTime)
  );
  const totalMinutes =
    difference.days() * 24 * 60 +
    difference.hours() * 60 +
    difference.minutes();
  const newTime = calculateNewTime(
    mode,
    selectTimeFor,
    duration,
    totalQuestions,
    totalMinutes
  );

  const isViewMode = viewMode === true;
  const showTimer = isViewMode === false;
  const showDesktopPauseControl = mode !== "live-test" && showTimer;
  const menuIconStateClass = drawerToggle
    ? classes.menuIconExpand
    : classes.changeColor;

  //change document name as per quiz name.
  useEffect(() => {
    fullScreenMode();
    if (name) {
      document.title = name;
    }
  }, []);

  const language =
    selectedLanguage === "en" || selectedLanguage === "hn"
      ? selectedLanguage
      : "en";

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  function goToAllTest() {
    history.push("/tests/result/" + quiz.id);
  }

  function handlePauseToggle() {
    handleQuizPauseChange(pauseQuiz === false);
  }

  if (isLoading) {
    return (
      <Box className={classes.quizHeader + " " + classes.quizLoader}>
        <QuizHeaderLoader />
      </Box>
    );
  }

  return (
    <Box className={headerClass}>
      {isViewMode && (
        <Box className={classes.solutionMode} onClick={goToAllTest}>
          <ArrowBackIcon />
          <span>Report Card</span>
        </Box>
      )}
      <Box
        onClick={toggleDrawerHandler}
        className={
          classes.menuIcon +
          " " +
          menuIconStateClass
        }
      >
        <img alt="question quiz" src={QuestionQuiz} />
      </Box>

      <Box className={classes.mobileView}>{name}</Box>

      <Box
        onClick={handlePauseToggle}
        className={classes.showMobile}
        id="playPauseIcon"
      >
        {renderPauseImage(pauseQuiz, mobilePause, undefined, "play quiz", "pause quiz")}
      </Box>

      {showTimer && (
        <QuizTimer
          viewMode={viewMode}
          mode={mode}
          setTimeTaken={setTimeTaken}
          submitQuiz={submitQuiz}
          duration={duration}
          usedTime={usedTime}
          pauseQuiz={pauseQuiz}
          difference={difference}
          handleClick={handleClick}
          selectTimeFor={selectTimeFor}
          hideNext={hideNext}
          reset={reset}
          setReset={setReset}
          totalMinutes={totalMinutes}
          slug={slug}
          newTime={newTime}
          courseId={courseId}
        />
      )}

      <Box className={classes.title}>{name}</Box>

      <Box
        onClick={handlePauseToggle}
        className={classes.showTab}
        id="playPauseIcon"
      >
        {renderPauseImage(pauseQuiz, mobilePause, undefined, "play quiz", "pause quiz")}
      </Box>

      {quizPage && (
        <Box>
          <Box className="notification-bar quiz-notification-bar">
            <ul className="right-menus">
              {showDesktopPauseControl && (
                <li
                  className={
                    classes.overrideHoverEffect +
                    " " +
                    classes.hideElementForMobile
                  }
                  id="playPauseIcon"
                >
                  <button
                    type="button"
                    onClick={handlePauseToggle}
                    className={classes.iconButtonReset}
                  >
                    {renderPauseImage(pauseQuiz, pauseImage, classes.pauseIcon, "pause quiz", "resume quiz")}
                  </button>
                </li>
              )}
              {quiz.isMultiLang &&
                (<li className={classes.overrideHoverEffect}>
                <LanguageSelect isInline />
              </li>)}
              <li className={classes.overrideHoverEffect}>
                <SubmitButton
                  slug={slug}
                  singleCourseId={courseId}
                  mode={mode}
                  hideSubmitButton={true}
                  viewMode={viewMode}
                  submitQuiz={submitQuiz}
                  quizId={quiz.id}
                />
              </li>
            </ul>
          </Box>
        </Box>
      )}
    </Box>
  );
}

const styles = (theme) => ({
  quizHeader: {
    backgroundColor: "#ffffff",
    borderBottom: "5px solid #0C3450",
    height: 66,
    marginBottom: 0,
    position: "fixed",
    top: 0,
    left: "320px",
    alignItems: "center",
    right: 0,
    zIndex: 999,
    display: "flex",
    justifyContent: "space-between",
    "@media (max-width: 767px)": {
      height: 55,
      alignItems: "center",
      zIndex: 9999,
      borderBottom: 0,
      borderTop: "5px solid #0C3450",
      left: 0,
      justifyContent: "space-around",
    },
    "@media (min-width: 768px) and (max-width: 800px)": {
      left: "9.7%",
      transition: "0.3s ease-in-out",
      zIndex: "inherit",
      borderTop: "5px solid #0C3450",
      borderBottom: 0,
    },
    "@media (min-width: 801px) and (max-width: 1024px)": {
      left: "50px",
      transition: "0.3s ease-in-out",
    },
  },
  quizHeaderExpand: {
    backgroundColor: "#ffffff",
    borderBottom: "5px solid #0C3450",
    height: 66,
    marginBottom: 0,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 999,
    display: "flex",
    justifyContent: "space-around",
    "@media (max-width: 800px)": {
      height: 55,
      alignItems: "center",
      zIndex: 9999,
      borderTop: "5px solid #0C3450",
      borderBottom: "0px",
      padding: "0 10px",
    },
    "@media (min-width: 801px) and (max-width: 1023px)": {
      left: "320px",
      transition: "0.3s ease-in-out",
      zIndex: "inherit",
    },
    "@media (min-width: 1024px) and (max-width: 1024px)": {
      left: 300,
      transition: "0.3s ease-in-out",
      zIndex: "inherit",
    },
    "@media (min-width: 1025px)": {
      left: "30px",
      transition: "0.3s ease-in-out",
      zIndex: "inherit",
    },
  },
  overrideHoverEffect: {
    "&:hover": {
      background: "none !important",
    },
  },
  logo: {
    float: "left",
    "& img": {
      width: "100%",
      maxWidth: 150,
      padding: "10px",
    },
    "@media (max-width:767px)": {
      display: "none",
      justifyContent: "flex-start",
    },
  },
  title: {
    float: "left",
    lineHeight: "66px",
    margin: "0 15px 0 0",
    color: "#0C3450",
    fontSize: 18,
    fontWeight: 500,
    maxWidth: "60%",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    overflow: "hidden",
    "@media (min-width: 280px) and (max-width:320px)": {
      fontSize: "16px",
      margin: "0 16% 0 0",
      display: "none",
    },
    "@media (min-width: 321px) and (max-width:767px)": {
      fontSize: "16px",
      margin: "0 28% 0 0",
      display: "none",
    },
    "@media (min-width: 768px) and (max-width:1500px)": {
      fontSize: "18px",
    },
    "@media (min-width: 1501px) and (max-width:1800px)": {
      fontSize: "20px",
    },
    "@media (min-width: 1801px)": {
      fontSize: "24px",
    },
  },

  mobileView: {
    float: "left",
    lineHeight: "66px",
    margin: "0 15px 0 0",
    color: "#0C3450",
    fontSize: 18,
    fontWeight: 500,
    maxWidth: "60%",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    overflow: "hidden",
    display: "none",
    "@media (min-width: 280px) and (max-width:767px)": {
      fontSize: "16px",
      margin: "0px 26px 0px 10px",
      display: "block",
    },
  },
  menuIcon: {
    display: "none",
    float: "left",
    "& i": {
      color: "#fff",
      cursor: "pointer",
      fontSize: 24,
    },
    "@media (max-width:767px)": {
      display: "flex",
    },
    "@media (min-width:768px) and (max-width:800px)": {
      display: "block",
      position: "relative",
      top: "23px",
      right: "56px",
    },
  },
  menuIconExpand: {
    display: "none",
    float: "left",
    "& i": {
      color: "#fff",
      cursor: "pointer",
      fontSize: 24,
    },
    "@media (max-width:767px)": {
      display: "flex",
    },
    "@media (min-width:768px) and (max-width:800px)": {
      display: "contents",
      position: "relative",
      top: "23px",
      right: "60px",
    },
  },
  pauseIcon: {
    position: "relative",
    top: "4px",
    right: "12px",
  },
  iconButtonReset: {
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },
  quizLoader: {
    height: "66px",
  },
  hideElementForMobile: {
    display: "inline-block",
    lineHeight: "8px !important",
    "&#fullscreenMode": {
      background: "#12a188",
      lineHeight: "8px !important",
      "&:hover": {
        background: "#12a188 !important",
      },
    },
    "&#playPauseIcon": {
      lineHeight: "8px !important",
      padding: "4px 6px!important",
      marginRight: 0,
      "& i": {
        marginLeft: 0,
      },
    },
    "@media (max-width: 767px)": {
      display: "none !important",
    },
  },
  solutionMode: {
    display: "none",
    "@media (max-width: 767px)": {
      display: "flex",
      alignItems: "center",
      left: 10,
      position: "absolute",
      color: "#fff",
      fontSize: 16,
      zIndex: 1,
      "& i": {
        color: "#fff",
        marginRight: 10,
        fontSize: 16,
      },
    },
  },
  showMobile: {
    display: "none",
    "@media (max-width: 767px)": {
      display: "block",
      alignSelf: "center",
    },
  },
  showTab: {
    display: "none",
    "@media (min-width: 768px) and (max-width: 1000px)": {
      display: "block",
      alignSelf: "center",
    },
  },
});

QuizHeader.propTypes = {
  quizPage: PropTypes.bool,
  name: PropTypes.string,
  viewMode: PropTypes.bool,
  mode: PropTypes.string,
  submitQuiz: PropTypes.func,
  drawerToggle: PropTypes.bool,
  duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  usedTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setTimeTaken: PropTypes.func,
  isLoading: PropTypes.bool,
  toggleDrawerHandler: PropTypes.func,
  pauseQuiz: PropTypes.bool,
  handleQuizPauseChange: PropTypes.func,
  slug: PropTypes.string,
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  totalQuestions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectTimeFor: PropTypes.string,
  handleClick: PropTypes.func,
  hideNext: PropTypes.bool,
  reset: PropTypes.bool,
  setReset: PropTypes.func,
  courseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default QuizHeader;
